import { SessionUserI } from '~src/interfaces/session.types';
import {
  AnswerSummary,
  BreadCrumb,
  HeaderInformation,
  HeaderRating,
  ReportEntityTypes,
  ReportsObj,
} from '~src/modules/reports/interfaces/header.interface';
import { SummaryTopic } from '~src/modules/reports/interfaces/topics.interface';
import {
  CHANGE_ANSWERS_SUMMARY,
  CHANGE_ENTITY_LOADING,
  CHANGE_GRAPH_VISIBILITY,
  CHANGE_HEADER_LOADING,
  CHANGE_HEADER_TAB_INDEX,
  CHANGE_PERIOD_SELECTOR_VISIBILITY,
  CHANGE_QUESTIONS_SUMMARY,
  CHANGE_RATING,
  CHANGE_RATING_VISIBILITY,
  CHANGE_SELECTED_PERIOD,
  OVERWRITE_BREADCRUMB_LINKS,
  SET_HEADER_INFORMATION,
  SUMMARY_FAILED,
  SUMMARY_REQUEST,
} from '~src/modules/reports/reportsHeader/constans/action.types';
import { PeriodValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { DirectSendData } from '~src/modules/reports/shared/directFeedback/DirectFeedback';
import { directFeedbackApi, reportingApi } from '~src/services/ApiController';
import { AsyncAction } from '~src/services/apiMiddleware';

import {
  ChangeEntityLoading,
  ChangeGraphVisibility,
  ChangeHeaderLoading,
  ChangeHeaderRating,
  ChangeHeaderTabIndex,
  ChangePeriodSelectorVisibility,
  ChangeRatingVisibility,
  FetchTopicsSummary,
  OverwriteBreadCrumbLinks,
  SetHeaderInformation,
} from './headerActions.interface';

export const setHeaderInformation: SetHeaderInformation = (
  headerInformation: Partial<HeaderInformation>,
) => ({
  type: SET_HEADER_INFORMATION,
  headerInformation,
});

export const overwriteBreadCrumbLinks: OverwriteBreadCrumbLinks = (links: BreadCrumb[]) => ({
  type: OVERWRITE_BREADCRUMB_LINKS,
  links,
});

export const changeRatingVisibility: ChangeRatingVisibility = (showRatingOnHeader: boolean) => ({
  type: CHANGE_RATING_VISIBILITY,
  showRatingOnHeader,
});

export const changeGraphVisibility: ChangeGraphVisibility = (showGraph: boolean) => ({
  type: CHANGE_GRAPH_VISIBILITY,
  showGraph,
});

export const changePeriodSelectorVisibility: ChangePeriodSelectorVisibility = (
  showPeriodSelector: boolean,
) => ({
  type: CHANGE_PERIOD_SELECTOR_VISIBILITY,
  showPeriodSelector,
});

export const changeHeaderRating: ChangeHeaderRating = (rating: HeaderRating) => ({
  type: CHANGE_RATING,
  rating,
});

export const changeSelectedPeriod = (selectedPeriod: PeriodValues) => ({
  type: CHANGE_SELECTED_PERIOD,
  selectedPeriod,
});

export const changeHeaderLoading: ChangeHeaderLoading = (loading: boolean) => ({
  type: CHANGE_HEADER_LOADING,
  loading,
});

export const changeEntityLoading: ChangeEntityLoading = (loading: boolean) => ({
  type: CHANGE_ENTITY_LOADING,
  loading,
});

export const changeHeaderTabIndex: ChangeHeaderTabIndex = (idx: number) => ({
  type: CHANGE_HEADER_TAB_INDEX,
  idx,
});

export const buildSummaryUrl = (
  subjectType: ReportEntityTypes,
  subject: ReportsObj,
  me: SessionUserI | null,
): string => {
  let url = '/reports';
  let surveyRoute = 'company';
  switch (subjectType) {
    case ReportEntityTypes.Group:
    case ReportEntityTypes.User:
      url += `/${subjectType}/${subject.id}`;
      break;

    case ReportEntityTypes.Survey:
      if (me && subject.surveyScope && subject.surveyScope !== 'admin') {
        surveyRoute = `user/${me.uuid}`;
      }
      url += `/${surveyRoute}/${subjectType}/${subject.id}`;
      break;
    default:
      url += `/${subjectType}`;
  }
  return url;
};

export const fetchTopicsSummary: FetchTopicsSummary<AsyncAction<SummaryTopic>> = (
  topicId,
  subjectType,
  answeredRange,
  surveyId,
  subject,
  me,
  showSelfAssessments,
) => {
  let url = buildSummaryUrl(subjectType, subject, me);
  url += `/topic/${topicId}/questions/summary`;
  const query = [];

  if (answeredRange) {
    query.push(`filterAnsweredRange=${answeredRange}`);
  }

  if (surveyId) {
    query.push(`filterSurvey=${surveyId}`);
  }
  if (typeof showSelfAssessments !== 'undefined') {
    query.push(`showSelfAssessments=${showSelfAssessments}`);
  }
  url += query.length > 0 ? '?' + query.join('&') : '';

  return {
    types: [SUMMARY_REQUEST, CHANGE_QUESTIONS_SUMMARY, SUMMARY_FAILED],
    callAPI: () => reportingApi.get(url),
  };
};

export const fetchAnswersSummary: FetchTopicsSummary<AsyncAction<AnswerSummary>> = (
  questionId,
  subjectType,
  answeredRange,
  surveyId,
  subject,
  me,
  showSelfAssessments,
) => {
  let url = buildSummaryUrl(subjectType, subject, me);
  url += `/question/${questionId}/answers/summary`;
  const query = [];
  if (answeredRange) {
    query.push(`filterAnsweredRange=${answeredRange}`);
  }
  if (surveyId) {
    query.push(`filterSurvey=${surveyId}`);
  }
  if (typeof showSelfAssessments !== 'undefined') {
    query.push(`showSelfAssessments=${showSelfAssessments}`);
  }

  url += query.length > 0 ? '?' + query.join('&') : '';

  return {
    types: [SUMMARY_REQUEST, CHANGE_ANSWERS_SUMMARY, SUMMARY_FAILED],
    callAPI: () => reportingApi.get(url),
  };
};

export const saveDirectFeedback = (data: DirectSendData) => directFeedbackApi.post('/api/feedbacks', data);
